import { Button, Slider } from '@arco-design/web-react';
import {
  IconRotateLeft,
  IconRotateRight,
} from '@arco-design/web-react/icon';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area, Point } from 'react-easy-crop/types';

import './ImageCropperPane.css';

export const CROP_ZOOM_MIN = 0.25;
export const CROP_ZOOM_MAX = 6;
export const CROP_ZOOM_STEP = 0.02;

type Props = {
  imageSrc: string;
  aspect: number;
  cropShape?: 'rect' | 'round';
  variant?: 'default' | 'wide';
  onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void;
  onCropAreaChange?: (croppedAreaPixels: Area | null, rotation: number) => void;
};

export function ImageCropperPane({
  imageSrc,
  aspect,
  cropShape = 'rect',
  variant = 'default',
  onCropComplete,
  onCropAreaChange,
}: Props) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setRotation(0);
    setZoom(1);
    setCroppedAreaPixels(null);
    onCropAreaChange?.(null, 0);
  }, [imageSrc]);

  useEffect(() => {
    if (croppedAreaPixels) {
      onCropAreaChange?.(croppedAreaPixels, rotation);
    }
  }, [rotation, croppedAreaPixels, onCropAreaChange]);

  const handleCropComplete = (croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
    onCropComplete(croppedArea, pixels);
    onCropAreaChange?.(pixels, rotation);
  };

  const handleRotate = (degree: number) => {
    setRotation((prev) => prev + degree);
  };

  const isWide = variant === 'wide' || aspect >= 2;
  const cropObjectFit = isWide ? 'horizontal-cover' as const : 'cover' as const;

  return (
    <>
      <div
        className={classNames('image-cropper-pane__crop-wrapper', {
          'image-cropper-pane__crop-wrapper--wide': isWide,
          'image-cropper-pane__crop-wrapper--square': aspect === 1,
        })}
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          rotation={rotation}
          zoom={zoom}
          aspect={aspect}
          minZoom={CROP_ZOOM_MIN}
          maxZoom={CROP_ZOOM_MAX}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onCropComplete={handleCropComplete}
          onZoomChange={setZoom}
          cropShape={cropShape}
          showGrid={cropShape === 'rect'}
          objectFit={cropObjectFit}
          zoomWithScroll
          restrictPosition={false}
        />
      </div>

      <div className="image-cropper-pane__settings">
        <div className="image-cropper-pane__rotate">
          <Button
            className="image-cropper-pane__icon-button"
            shape="round"
            aria-label="Rotate left"
            onClick={() => handleRotate(-90)}
          >
            <IconRotateLeft />
          </Button>
          <Button
            className="image-cropper-pane__icon-button"
            shape="round"
            aria-label="Rotate right"
            onClick={() => handleRotate(90)}
          >
            <IconRotateRight />
          </Button>
        </div>

        <div className="image-cropper-pane__zoom">
          <span className="image-cropper-pane__zoom-label">Zoom</span>
          <Slider
            className="image-cropper-pane__zoom-slider"
            min={CROP_ZOOM_MIN}
            max={CROP_ZOOM_MAX}
            step={CROP_ZOOM_STEP}
            value={zoom}
            onChange={(value) => {
              if (typeof value === 'number') {
                setZoom(value);
              }
            }}
          />
        </div>
      </div>
    </>
  );
}
